import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { useAuth } from '../context/AuthContext';
import { registerPushToken } from '../lib/api';
import { currentPushPermission, isPushSupported, syncPushToken } from '../lib/push';
import {
  notificationRouteOrInbox,
  parseNotificationRouteData
} from '../lib/notificationRoute';

/**
 * Wires native push notifications for the signed-in user. It keeps the FCM token
 * registered and fresh and routes a tapped push to the right screen — but it never
 * prompts for permission (that happens contextually via `requestPushPermission`).
 * A no-op off-device. Mounted once inside the authenticated app chrome.
 */
export function usePushNotifications(): void {
  const { uid } = useAuth();
  const navigate = useNavigate();

  // Listeners (token refresh + notification taps). Attached once on native.
  useEffect(() => {
    if (!isPushSupported()) return;
    let cancelled = false;
    const handles: Array<{ remove: () => Promise<void> }> = [];

    void (async () => {
      const onToken = await FirebaseMessaging.addListener('tokenReceived', (event) => {
        if (event.token) registerPushToken(event.token, Capacitor.getPlatform()).catch(() => undefined);
      });
      const onTap = await FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
        const { type, data } = parseNotificationRouteData(event.notification?.data);
        navigate(notificationRouteOrInbox(type, data));
      });
      if (cancelled) {
        await onToken.remove().catch(() => undefined);
        await onTap.remove().catch(() => undefined);
        return;
      }
      handles.push(onToken, onTap);
    })();

    return () => {
      cancelled = true;
      for (const h of handles) h.remove().catch(() => undefined);
    };
  }, [navigate]);

  // When signed in and permission is already granted, silently (re)register the token.
  useEffect(() => {
    if (!uid || !isPushSupported()) return;
    void (async () => {
      if ((await currentPushPermission()) === 'granted') await syncPushToken();
    })();
  }, [uid]);
}
